import {
  generateJSON,
  getHTMLFromFragment,
  type AnyExtension,
  type Editor,
  type JSONContent,
} from "@tiptap/react";
import type { AnalysisDocument, AnalysisDocumentSection } from "@/api/analysis-api";

/**
 * Moving a document between the server's shape (an ordered array of sections,
 * each holding HTML) and the editor's (one ProseMirror document of section
 * nodes) — and back again.
 *
 * Kept apart from the editor component because this is where the correctness
 * lives: a section that round-trips wrong loses someone's writing.
 */

/** A section with no content still needs a block, or the node is invalid. */
const EMPTY_BLOCK: JSONContent = { type: "paragraph" };

/**
 * Whether a person's writing is the newest thing in this section.
 *
 * Not simply "has `edited_at`". A forced regenerate overwrites the section but
 * leaves `edited_at` in place, so testing it alone left the "Edited" badge on
 * text the model had just rewritten. Mirrors `hasEditsSinceGenerated` in the
 * backend, which decides the same question for skipping.
 */
export const hasEditsSinceGenerated = (
  section: Pick<AnalysisDocumentSection, "edited_at" | "generated_at">
): boolean => {
  if (!section.edited_at) return false;
  if (!section.generated_at) return true;
  return new Date(section.edited_at) > new Date(section.generated_at);
};

export const sectionAttributes = (section: AnalysisDocumentSection) => ({
  sectionId: section.section_id,
  title: section.title,
  status: section.status,
  orphaned: Boolean(section.orphaned),
  edited: hasEditsSinceGenerated(section),
  error: section.error ?? "",
  // Chrome, like status and error — the server owns it, and it is never
  // serialised back into the section's HTML. That is what keeps a citation
  // safe from an edit to the prose it sits under.
  sources: section.sources ?? [],
});

/** Parse one section's stored HTML into the blocks that go inside its node. */
export const sectionContent = (
  html: string,
  extensions: AnyExtension[]
): JSONContent[] => {
  const trimmed = (html ?? "").trim();
  if (!trimmed) return [EMPTY_BLOCK];

  const parsed = generateJSON(trimmed, extensions) as JSONContent;
  const content = parsed.content ?? [];
  return content.length ? content : [EMPTY_BLOCK];
};

/** The whole document, ready to hand to the editor. */
export const buildDocumentContent = (
  sections: AnalysisDocumentSection[],
  extensions: AnyExtension[]
): JSONContent => ({
  type: "doc",
  content: sections.length
    ? sections.map((section) => ({
        type: "analysisSection",
        attrs: sectionAttributes(section),
        content: sectionContent(section.html, extensions),
      }))
    : [EMPTY_BLOCK],
});

/**
 * Each section's current HTML, keyed by section id.
 *
 * Serialises each node's *contents*, not the node itself: the section wrapper
 * and its title are chrome the server already knows about, and writing them into
 * the stored HTML would duplicate the heading on every save.
 */
export const serializeSections = (editor: Editor): Record<string, string> => {
  const result: Record<string, string> = {};

  editor.state.doc.forEach((node) => {
    if (node.type.name !== "analysisSection") return;
    const sectionId = node.attrs.sectionId;
    if (!sectionId) return;
    result[String(sectionId)] = getHTMLFromFragment(node.content, editor.schema);
  });

  return result;
};

/**
 * The section the cursor is in, and its current HTML.
 *
 * Serialising one section is cheap enough to do on every keystroke, which is
 * what lets an edit survive the editor being destroyed before the debounced save
 * has run.
 */
export const activeSectionHtml = (
  editor: Editor
): { sectionId: string; html: string } | null => {
  const { $from } = editor.state.selection;

  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth);
    if (node.type.name !== "analysisSection") continue;
    const sectionId = node.attrs.sectionId;
    if (!sectionId) return null;
    return {
      sectionId: String(sectionId),
      html: getHTMLFromFragment(node.content, editor.schema),
    };
  }

  return null;
};

export interface SelectionInfo {
  sectionId: string;
  sectionTitle: string;
  from: number;
  to: number;
  /** The selection as HTML, which is what the model is asked to revise. */
  html: string;
  /** The same selection as text, which is what the diff is computed on. */
  text: string;
}

/**
 * What is selected, and which section it belongs to.
 *
 * Null for an empty selection, and null for a selection that spans two sections
 * — a revision is scoped to one section so that accepting it is one section's
 * write. `isolating` on the section node makes crossing them hard, but a
 * select-all would do it.
 */
/**
 * Grow a selection out to whole words.
 *
 * A drag that stops mid-word hands the model half of one — and given "…how it
 * mak" it completes the sentence rather than the fragment, so accepting the
 * revision would splice it in front of the "es money." still sitting in the
 * document. Snapping first means the passage is always something a model can
 * sensibly rewrite.
 *
 * Expansion stays inside the text block at each end, so it can never cross into
 * a neighbouring paragraph or out of the section.
 */
export const expandToWordBoundaries = (
  editor: Editor,
  from: number,
  to: number
): { from: number; to: number } => {
  const { doc } = editor.state;
  const isWordChar = (text: string) => text.length > 0 && !/\s/.test(text);

  const startLimit = doc.resolve(from).start();
  let start = from;
  while (start > startLimit && isWordChar(doc.textBetween(start - 1, start))) {
    start -= 1;
  }

  const endLimit = doc.resolve(to).end();
  let end = to;
  while (end < endLimit && isWordChar(doc.textBetween(end, end + 1))) {
    end += 1;
  }

  return { from: start, to: end };
};

export const selectionInfo = (editor: Editor): SelectionInfo | null => {
  const { state } = editor;
  const { empty } = state.selection;
  if (empty) return null;

  const { from, to } = expandToWordBoundaries(
    editor,
    state.selection.from,
    state.selection.to
  );

  const startSection = enclosingSection(editor, from);
  const endSection = enclosingSection(editor, to);
  if (!startSection || startSection.sectionId !== endSection?.sectionId) {
    return null;
  }

  return {
    sectionId: startSection.sectionId,
    sectionTitle: startSection.title,
    from,
    to,
    // Sliced from the expanded range, not taken from `selection.content()` —
    // that would be the original, half-word range.
    html: getHTMLFromFragment(state.doc.slice(from, to).content, state.schema),
    text: state.doc.textBetween(from, to, "\n", " "),
  };
};

const enclosingSection = (
  editor: Editor,
  position: number
): { sectionId: string; title: string } | null => {
  const resolved = editor.state.doc.resolve(position);

  for (let depth = resolved.depth; depth > 0; depth -= 1) {
    const node = resolved.node(depth);
    if (node.type.name !== "analysisSection") continue;
    if (!node.attrs.sectionId) return null;
    return {
      sectionId: String(node.attrs.sectionId),
      title: String(node.attrs.title ?? "this section"),
    };
  }

  return null;
};

/**
 * Apply a revision to the range it was asked for — but only if that range still
 * holds the text it did when the revision was requested.
 *
 * The document stays editable while the author reads the diff, and an edit made
 * in the meantime shifts every position after it. Writing to a stale range would
 * drop a revision on top of unrelated prose, which is precisely the "the AI
 * mangled my memo" failure the confirmation step exists to prevent. Refusing is
 * recoverable; a misplaced write is not.
 */
export const replaceRangeIfUnchanged = (
  editor: Editor,
  range: { from: number; to: number },
  expectedText: string,
  html: string
): boolean => {
  const { doc } = editor.state;
  if (range.to > doc.content.size) return false;

  const current = doc.textBetween(range.from, range.to, "\n", " ");
  if (current !== expectedText) return false;

  return editor
    .chain()
    .focus()
    .insertContentAt({ from: range.from, to: range.to }, html)
    .run();
};

/** Position of a section node in the document, or null if it is not there. */
export const findSectionPosition = (
  editor: Editor,
  sectionId: string
): { pos: number; size: number } | null => {
  let found: { pos: number; size: number } | null = null;

  editor.state.doc.forEach((node, offset) => {
    if (
      node.type.name === "analysisSection" &&
      String(node.attrs.sectionId) === sectionId
    ) {
      found = { pos: offset, size: node.nodeSize };
    }
  });

  return found;
};

/**
 * Replace one section's content and status with what the server has.
 *
 * Used when a worker finishes a section while the document is open. Only that
 * section's range is touched, so edits elsewhere — including ones not yet
 * autosaved — survive.
 */
export const applyServerSection = (
  editor: Editor,
  section: AnalysisDocumentSection,
  extensions: AnyExtension[]
): boolean => {
  const target = findSectionPosition(editor, section.section_id);
  if (!target) return false;

  const attrs = sectionAttributes(section);
  const content = sectionContent(section.html, extensions);

  return editor
    .chain()
    .command(({ tr }) => {
      // Inside the node: +1 past the opening token, -1 before the closing one.
      tr.replaceWith(
        target.pos + 1,
        target.pos + target.size - 1,
        content.map((block) => editor.schema.nodeFromJSON(block))
      );
      // Re-resolve after the replace — the node's size has just changed.
      const node = tr.doc.nodeAt(target.pos);
      if (node) tr.setNodeMarkup(target.pos, undefined, attrs);
      return true;
    })
    .run();
};

/** Update a section's chrome without touching its content. */
export const applySectionAttributes = (
  editor: Editor,
  section: AnalysisDocumentSection
): boolean => {
  const target = findSectionPosition(editor, section.section_id);
  if (!target) return false;

  return editor
    .chain()
    .command(({ tr }) => {
      tr.setNodeMarkup(target.pos, undefined, sectionAttributes(section));
      return true;
    })
    .run();
};

/**
 * The document as plain text, for the chat panel.
 *
 * Section titles are included — they are half the meaning of a memo, and the
 * chat cannot see the chrome that renders them.
 */
export const documentPlainText = (document: AnalysisDocument): string => {
  const parts = document.sections
    .filter((section) => section.status !== "pending")
    .map((section) => {
      const body = htmlToPlainText(section.html);
      return body ? `## ${section.title}\n${body}` : `## ${section.title}\n(empty)`;
    });

  return [`# ${document.template_name}`, ...parts].join("\n\n");
};

/**
 * HTML to readable text.
 *
 * Deliberately not a regex strip: table cells and list items run together into
 * one unreadable line without block-level separators, and the chat is being
 * asked to reason about the numbers in those tables.
 */
export const htmlToPlainText = (html: string): string => {
  const trimmed = (html ?? "").trim();
  if (!trimmed) return "";

  const parsed = new DOMParser().parseFromString(trimmed, "text/html");

  parsed.body.querySelectorAll("td, th").forEach((cell) => {
    cell.append(document.createTextNode(" | "));
  });
  parsed.body
    .querySelectorAll("p, div, li, tr, h1, h2, h3, h4, blockquote, hr")
    .forEach((block) => {
      block.append(document.createTextNode("\n"));
    });

  return (parsed.body.textContent ?? "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};
